import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type {
  ListNewsArticlesParams,
  NewsArticle,
  NewsArticleFormValues,
} from './schema';
import type { CommonResponse } from '@/types/api';

const ROOT_KEY = 'newsArticles';
const listKey = (params: ListNewsArticlesParams) => [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useNewsArticles(params: ListNewsArticlesParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listNewsArticles(params),
    placeholderData: (prev) => prev, // keep current page visible while next loads
  });
}

export function useNewsArticle(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getNewsArticle(id as string),
    enabled: !!id,
  });
}

export function useCreateNewsArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: NewsArticleFormValues) => api.createNewsArticle(values),
    onSuccess: (created, variables) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      toast.success('News article created');
      // Backend auto-suffixes slug on collision; surface the rename to the editor.
      if (variables.slug && created.slug && variables.slug !== created.slug) {
        toast.info(`Slug "${variables.slug}" was taken — saved as "${created.slug}".`);
      }
    },
    onError: (err) => toast.error(describeError(err, 'Failed to create news article')),
  });
}

export function useUpdateNewsArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: NewsArticleFormValues }) =>
      api.updateNewsArticle(id, values),
    onSuccess: (updated) => {
      // Invalidate (don't merge): the update response is the base NewsArticle
      // shape and lacks `translations`/`tags`, so a shallow merge keeps the
      // previous (now-stale) translations alive in the cache.
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      toast.success('News article updated');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to update news article')),
  });
}

// Optimistic delete: snapshot every cached list, drop the row, restore on failure.
export function useDeleteNewsArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteNewsArticle(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: [ROOT_KEY, 'list'] });
      const snapshots = queryClient.getQueriesData<CommonResponse<NewsArticle>>({
        queryKey: [ROOT_KEY, 'list'],
      });
      for (const [key, value] of snapshots) {
        if (!value) continue;
        queryClient.setQueryData<CommonResponse<NewsArticle>>(key, {
          ...value,
          rowData: value.rowData.filter((r) => r.id !== id),
        });
      }
      return { snapshots };
    },
    onError: (err, _id, ctx) => {
      ctx?.snapshots.forEach(([key, value]) => queryClient.setQueryData(key, value));
      toast.error(describeError(err, 'Failed to delete news article'));
    },
    onSettled: (_data, _err, id) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.removeQueries({ queryKey: detailKey(id) });
    },
    onSuccess: () => toast.success('News article deleted'),
  });
}
